import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, FileSpreadsheet, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, has } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";

const REPORTS = [
  { kind: "weekly", title: "Weekly pastoral monitoring", days: 7, perm: "report:generate" },
  { kind: "monthly", title: "Monthly PPC meeting report", days: 30, perm: "report:generate" },
  { kind: "quarterly", title: "Quarterly planning & evaluation", days: 91, perm: "report:generate" },
  { kind: "annual", title: "Annual parish pastoral report", days: 365, perm: "report:generate" },
  { kind: "gkk", title: "GKK-level pastoral review", days: 91, perm: ["report:generate", "report:own_gkk"], needsGkk: true },
  { kind: "ministry", title: "Ministry-specific accomplishments", days: 91, perm: "report:generate", needsMinistry: true },
  { kind: "diocesan", title: "Diocesan pastoral reporting", days: 365, perm: "report:diocesan" },
];
const iso = (d) => d.toLocaleDateString("en-CA");
const defaults = (days) => ({ start: iso(new Date(Date.now() - days * 864e5)), end: iso(new Date()) });

const download = async (kind, format, params, setBusy) => {
  setBusy(`${kind}-${format}`);
  try {
    const r = await api.get(`/reports/${kind}`, { params: { ...params, format }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `mabuhay-${kind}-${params.end}.${format}`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    const text = e?.response?.data ? await e.response.data.text?.() : "";
    toast.error((text && JSON.parse(text).detail) || "Could not generate the report");
  } finally { setBusy(""); }
};

const ReportCard = ({ r, gkks, user, busy, setBusy }) => {
  const [p, setP] = useState({ ...defaults(r.days), gkk_id: user.role === "gkk_president" ? user.gkk_id || "" : "", ministry: "" });
  const params = { start: p.start, end: p.end, ...(r.needsGkk ? { gkk_id: p.gkk_id } : {}), ...(r.needsMinistry ? { ministry: p.ministry } : {}) };
  const ready = (!r.needsGkk || p.gkk_id) && (!r.needsMinistry || p.ministry.trim());
  return (
    <div className="rounded-xl border border-amber-700/15 bg-white p-5" data-testid={`report-card-${r.kind}`}>
      <p className="font-serif-display text-xl font-semibold text-amber-950">{r.title}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Input type="date" value={p.start} onChange={(e) => setP({ ...p, start: e.target.value })} data-testid={`${r.kind}-start-input`} />
        <Input type="date" value={p.end} onChange={(e) => setP({ ...p, end: e.target.value })} data-testid={`${r.kind}-end-input`} />
      </div>
      {r.needsGkk && (
        <Select value={p.gkk_id || "none"} disabled={user.role === "gkk_president"} onValueChange={(v) => setP({ ...p, gkk_id: v === "none" ? "" : v })}>
          <SelectTrigger className="mt-2 bg-white" data-testid="report-gkk-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="none" disabled>Choose a GKK</SelectItem>{gkks.map((g) => <SelectItem key={g.gkk_id} value={g.gkk_id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {r.needsMinistry && <Input className="mt-2" placeholder="Ministry or office, as written in action plans" value={p.ministry} onChange={(e) => setP({ ...p, ministry: e.target.value })} data-testid="report-ministry-input" />}
      <div className="mt-3 flex gap-2">
        <Button size="sm" disabled={!ready || !!busy} onClick={() => download(r.kind, "pdf", params, setBusy)} className="bg-amber-900 hover:bg-[#5B210B]" data-testid={`${r.kind}-pdf-button`}><FileText className="h-4 w-4 mr-1" />{busy === `${r.kind}-pdf` ? "Preparing…" : "PDF"}</Button>
        <Button size="sm" variant="outline" disabled={!ready || !!busy} onClick={() => download(r.kind, "xlsx", params, setBusy)} data-testid={`${r.kind}-xlsx-button`}><FileSpreadsheet className="h-4 w-4 mr-1" />{busy === `${r.kind}-xlsx` ? "Preparing…" : "Excel"}</Button>
      </div>
    </div>
  );
};

export default function Reports() {
  const { user } = useAuth();
  const [gkks, setGkks] = useState([]);
  const [busy, setBusy] = useState("");
  const [b, setB] = useState(defaults(30));
  useEffect(() => { api.get("/gkks").then((r) => setGkks(r.data)); }, []);
  const allowed = REPORTS.filter((r) => has(user, ...[].concat(r.perm)));
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Reports and evaluation" title="Pastoral Report Generator" testId="reports-header">
        Generated on demand from recorded data. Each report contains the executive summary, findings, priorities, actions, programs, accomplishments, unresolved needs, recommendations, tables and graphs, and action items. All reports are drafts for review by pastoral leaders.
      </PageHeader>
      {has(user, "report:briefing") && (
        <section className="rounded-xl bg-amber-900 p-6 text-amber-50" data-testid="briefing-card">
          <p className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-200"><ScrollText className="h-4 w-4" />One page</p>
          <p className="mt-1 font-serif-display text-3xl font-semibold">Pastoral Briefing for the Parish Priest</p>
          <p className="mt-1 text-sm text-amber-100">Only critical concerns, decisions needed and pending pastoral actions.</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input type="date" className="w-40 bg-white text-stone-900" value={b.start} onChange={(e) => setB({ ...b, start: e.target.value })} data-testid="briefing-start-input" />
            <Input type="date" className="w-40 bg-white text-stone-900" value={b.end} onChange={(e) => setB({ ...b, end: e.target.value })} data-testid="briefing-end-input" />
            <Button disabled={!!busy} onClick={() => download("briefing", "pdf", b, setBusy)} className="bg-amber-50 text-amber-950 hover:bg-white" data-testid="briefing-pdf-button">{busy === "briefing-pdf" ? "Preparing…" : "Download briefing (PDF)"}</Button>
          </div>
        </section>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{allowed.map((r) => <ReportCard key={r.kind} r={r} gkks={gkks} user={user} busy={busy} setBusy={setBusy} />)}</div>
      {has(user, "analyst:use") && <Link to="/analyst/drafts" className="inline-block text-sm font-semibold text-amber-900 underline" data-testid="reports-ppc-draft-link">Prefer a written narrative? Let the Pastoral Analyst draft the monthly PPC summary for review →</Link>}
      {!allowed.length && <p className="text-sm text-stone-500">You do not have access to reports.</p>}
      <p className="text-xs text-stone-500">Scheduled automatic reports are not turned on. They can be added later with administrator approval.</p>
    </div>
  );
}
